import React, { useCallback, useEffect, useState } from "react";
import { useTimer } from "react-timer-hook";
import { useSendOTPWithRecaptcha, useVerifyOTPMutation } from "../../../store/auth/otp.api";
import { useRecaptchaDismissalDetector } from "../../../hooks/useRecaptchaDismissalDetector";
import { RegistrationShell } from "../components/RegistrationShell";
import { RegistrationIcon } from "../components/RegistrationIcon";
import { OtpDigitInput } from "../components/OtpDigitInput";
import { useGeniusRegistration } from "../GeniusRegistrationContext";
import { INDIA_ISD, REGISTRATION_STEPS } from "../constants";
import { validateMobile } from "../validations";
import styles from "../GeniusRegistrationModal.module.css";

const OTP_TIMER_SEC = 30;
const RECAPTCHA_BODY_CLASS = "genius-recaptcha-active";

const setRecaptchaActive = (active) => {
  document.body.classList.toggle(RECAPTCHA_BODY_CLASS, active);
};

export const PhoneOtpStep = () => {
  const {
    shared,
    updateShared,
    goToOtp,
    goToForm,
    closeRegistration,
    step,
    setStep
  } = useGeniusRegistration();

  const isOtpPhase = step === REGISTRATION_STEPS.OTP;

  const [mobile, setMobile] = useState(shared.mobile || "");
  const [otp, setOtp] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);

  const [sendOTP] = useSendOTPWithRecaptcha();
  const [verifyOTP] = useVerifyOTPMutation();

  const { restart, seconds, isRunning } = useTimer({
    expiryTimestamp: new Date(),
    autoStart: false,
    onExpire: () => {}
  });

  const { setupDismissalListener, cleanup } = useRecaptchaDismissalDetector({
    onDismissed: () => {
      setRecaptchaActive(false);
      setLoading(false);
      setPhoneError("Security verification cancelled. Please try again.");
    }
  });

  useEffect(
    () => () => {
      setRecaptchaActive(false);
      cleanup();
    },
    [cleanup]
  );

  const mobileValidation = validateMobile(mobile);

  const handleMobileChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobile(digits);
    setPhoneError("");
  };

  const startTimer = useCallback(() => {
    const time = new Date();
    time.setSeconds(time.getSeconds() + OTP_TIMER_SEC);
    restart(time);
  }, [restart]);

  const handleSendOtp = async () => {
    if (!mobileValidation.valid) {
      setPhoneError(mobileValidation.message || "Enter a valid 10-digit mobile number");
      return;
    }

    if (!shared.termsAccepted) {
      setPhoneError("Please accept the Terms & Conditions to receive OTP.");
      return;
    }

    setLoading(true);
    setPhoneError("");
    setOtpError("");
    setupDismissalListener();
    setRecaptchaActive(true);

    try {
      await sendOTP({ phoneNumber: mobile, isdCode: INDIA_ISD });
      updateShared({ mobile });
      startTimer();
      goToOtp();
      setOtp("");
    } catch (error) {
      const err = error?.error?.data || error?.error || error;
      const message =
        err?.message ||
        err?.detail ||
        (typeof err === "string" ? err : "Failed to send OTP. Please try again.");

      setPhoneError(
        message.toLowerCase().includes("recaptcha") || message.toLowerCase().includes("security verification")
          ? "Security verification failed. Complete the reCAPTCHA check and try again."
          : message
      );
    } finally {
      setRecaptchaActive(false);
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (isRunning) return;
    await handleSendOtp();
  };

  const handleVerify = async () => {
    if (otp.length !== 4) {
      setOtpError("Enter the 4-digit OTP");
      return;
    }

    setLoading(true);
    setOtpError("");

    try {
      const result = await verifyOTP({
        phoneNumber: mobile,
        isdCode: INDIA_ISD,
        otpCode: otp
      });

      if (result?.error) {
        setOtpError(result.error?.data?.message || "Incorrect OTP. Please try again.");
      } else if (result?.data) {
        updateShared({ mobile, otpVerified: true });
        goToForm();
      } else {
        setOtpError("Incorrect OTP. Please try again.");
      }
    } catch {
      setOtpError("Incorrect OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditPhone = () => {
    setOtp("");
    setOtpError("");
    setStep(REGISTRATION_STEPS.PHONE);
  };

  const primaryLabel = isOtpPhase ? "Verify & continue" : "Get OTP";
  const primaryDisabled = isOtpPhase
    ? otp.length !== 4 || loading
    : !mobileValidation.valid || loading;
  const primaryAction = isOtpPhase ? handleVerify : handleSendOtp;

  return (
    <RegistrationShell title="Register now" onClose={closeRegistration} centerTitle>
      {!isOtpPhase ? (
        <>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="genius-reg-phone">
              Phone number
            </label>
            <div className={`${styles.phoneBar} ${phoneError ? styles.phoneBarError : ""}`}>
              <span className={styles.isdPrefix}>{INDIA_ISD}</span>
              <span className={styles.phoneDivider} aria-hidden />
              <input
                id="genius-reg-phone"
                type="tel"
                inputMode="numeric"
                className={styles.phoneInput}
                placeholder="Enter mobile number"
                value={mobile}
                onChange={handleMobileChange}
                maxLength={10}
                autoComplete="tel-national"
              />
            </div>
            {phoneError ? <p className={styles.errorText}>{phoneError}</p> : null}
          </div>

          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={shared.whatsappConsent}
              onChange={(e) => updateShared({ whatsappConsent: e.target.checked })}
            />
            <span>Notify me on WhatsApp</span>
          </label>

          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={shared.termsAccepted}
              onChange={(e) => updateShared({ termsAccepted: e.target.checked })}
            />
            <span>
              By signing up, you agree with our{" "}
              <a href="/terms-and-conditions" target="_blank" rel="noreferrer">
                <strong>T&amp;C</strong>
              </a>{" "}
              and{" "}
              <a href="/privacy-policy" target="_blank" rel="noreferrer">
                <strong>PP</strong>
              </a>
            </span>
          </label>
        </>
      ) : (
        <>
          <div className={styles.field}>
            <label className={styles.label}>Phone number</label>
            <div className={styles.phoneLockedBar}>
              <span>
                {INDIA_ISD} {mobile}
              </span>
              <button type="button" className={styles.editPhoneBtn} onClick={handleEditPhone}>
                <RegistrationIcon
                  name="editPhone"
                  className={styles.editPhoneIcon}
                  fallback={() => null}
                />
                Edit
              </button>
            </div>
          </div>

          <p className={styles.otpSectionLabel}>Enter the OTP sent to your mobile number</p>
          <div className={styles.otpWithResend}>
            <OtpDigitInput value={otp} onChange={setOtp} error={Boolean(otpError)} disabled={loading} />
            <div className={styles.otpResendColumn}>
              <button
                type="button"
                className={styles.resendBtn}
                disabled={isRunning || loading}
                onClick={handleResend}
              >
                Resend OTP
              </button>
              {isRunning ? (
                <span className={styles.resendTimer}>
                  ({String(seconds).padStart(2, "0")} sec)
                </span>
              ) : null}
            </div>
          </div>

          {otpError ? <p className={styles.errorText}>{otpError}</p> : null}
        </>
      )}

      <button
        type="button"
        className={`${styles.primaryBtn} ${
          primaryDisabled ? styles.primaryBtnDisabled : styles.primaryBtnActive
        }`}
        disabled={primaryDisabled}
        onClick={primaryAction}
      >
        {loading ? "Please wait..." : primaryLabel}
      </button>
    </RegistrationShell>
  );
};
